import React, { useState, useEffect, useRef } from 'react';
import { TrendingDown, TrendingUp, Remove } from '@mui/icons-material';
import { customOptionPrimaryLabel } from './customOptionHelpers';

const RiskScoreSlider = ({ questionType, customOptions = [], predefinedOptionsInfo = {}, riskScores = {}, onRiskScoreChange }) => {
    const [scores, setScores] = useState(riskScores || {});
    const [draggingOption, setDraggingOption] = useState(null);
    const sliderRef = useRef(null);

    // Get options based on question type
    const getOptions = () => {
        const predefinedTypes = {
            'yesNo': ['Yes', 'No', 'N/A'],
            'YN/F/T': ['Yes', 'No', 'N/A'],
            'D/ND': ['Disclosure', 'No Disclosure', 'Not Sure'],
            '1to5': ['1', '2', '3', '4', '5']
        };

        if (predefinedTypes[questionType]) {
            return predefinedTypes[questionType];
        }

        if (['DropDown', 'Radio', 'Multi'].includes(questionType)) {
            return customOptions.map(customOptionPrimaryLabel).filter(v => v && v.trim());
        }

        return [];
    };

    const options = getOptions();

    // Sync and initialize scores - merged into one effect to prevent race conditions
    useEffect(() => {
        // Start with riskScores prop values (from backend in edit mode)
        const initialScores = { ...riskScores };

        // Fill in any missing options with 0
        options.forEach(option => {
            if (initialScores[option] === undefined || initialScores[option] === null) {
                initialScores[option] = 0;
            }
        });

        // Remove any scores for options that no longer exist
        Object.keys(initialScores).forEach(key => {
            if (!options.includes(key)) {
                delete initialScores[key];
            }
        });

        // Only update if scores actually changed
        const scoresChanged = JSON.stringify(scores) !== JSON.stringify(initialScores);
        if (scoresChanged) {
            setScores(initialScores);
        }
    }, [riskScores, options.length, questionType]);

    const getRiskColor = (value) => {
        if (value <= -3) return '#d32f2f';
        if (value <= -1) return '#f57c00';
        if (value === 0) return '#757575';
        if (value <= 2) return '#fbc02d';
        return '#388e3c';
    };

    const getRiskLabel = (value) => {
        if (value <= -4) return 'Very High Risk';
        if (value <= -2) return 'High Risk';
        if (value < 0) return 'Medium Risk';
        if (value === 0) return 'Neutral';
        if (value <= 2) return 'Low Risk';
        return 'Very Low Risk';
    };

    const valueToPosition = (value) => {
        // Convert value (-5 to 5) to percentage (0 to 100)
        return ((value + 5) / 10) * 100;
    };

    const positionToValue = (position) => {
        // Convert percentage (0 to 100) to value (-5 to 5)
        const value = (position / 100) * 10 - 5;
        return Math.round(value);
    };

    const handleMouseDown = (option) => {
        setDraggingOption(option);
    };

    const handleMouseMove = (e) => {
        if (!draggingOption || !sliderRef.current) return;

        const rect = sliderRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const position = Math.max(0, Math.min(100, (x / rect.width) * 100));
        const newValue = positionToValue(position);

        const newScores = { ...scores, [draggingOption]: newValue };
        setScores(newScores);
        onRiskScoreChange(newScores);
    };

    const handleMouseUp = () => {
        setDraggingOption(null);
    };

    const handleTouchStart = (option) => {
        setDraggingOption(option);
    };

    const handleTouchMove = (e) => {
        if (!draggingOption || !sliderRef.current) return;

        const touch = e.touches[0];
        const rect = sliderRef.current.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const position = Math.max(0, Math.min(100, (x / rect.width) * 100));
        const newValue = positionToValue(position);

        const newScores = { ...scores, [draggingOption]: newValue };
        setScores(newScores);
        onRiskScoreChange(newScores);
    };

    const handleTouchEnd = () => {
        setDraggingOption(null);
    };

    useEffect(() => {
        if (draggingOption) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.addEventListener('touchmove', handleTouchMove);
            document.addEventListener('touchend', handleTouchEnd);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleTouchEnd);
            };
        }
    }, [draggingOption, scores]);

    if (options.length === 0) {
        return null;
    }

    return (
        <div
            className="br12px p1-5rem mt1-5rem"
            style={{
                background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                border: '1px solid #e0e0e0'
            }}
        >
            {/* Header */}
            <div className="df aic mb1-5rem" style={{ gap: '10px' }}>
                <div
                    className="df aic jcc"
                    style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                    }}
                >
                    <TrendingUp style={{ fontSize: '24px', color: 'white' }} />
                </div>
                <div>
                    <h4 className="fs1rem fw600 mb0-25rem" style={{ color: '#333', margin: '0 0 0.25rem 0' }}>
                        Assign Risk Scores
                    </h4>
                    <p className="fs0-8rem" style={{ color: '#666', margin: 0 }}>
                        Drag each option along the slider to assign risk values (-5 to +5)
                    </p>
                </div>
            </div>

            {/* Single Slider Track with Multiple Thumbs */}
            <div className="mb2rem">
                {/* Scale Labels */}
                <div className="df jcsb mb1rem" style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                    <div className="df aic" style={{ gap: '6px' }}>
                        <TrendingDown style={{ fontSize: '18px', color: '#d32f2f' }} />
                        <span style={{ fontSize: '0.85rem', color: '#d32f2f', fontWeight: '600' }}>-5</span>
                    </div>
                    <div className="df aic" style={{ gap: '6px' }}>
                        <Remove style={{ fontSize: '18px', color: '#757575' }} />
                        <span style={{ fontSize: '0.85rem', color: '#757575', fontWeight: '600' }}>0</span>
                    </div>
                    <div className="df aic" style={{ gap: '6px' }}>
                        <TrendingUp style={{ fontSize: '18px', color: '#388e3c' }} />
                        <span style={{ fontSize: '0.85rem', color: '#388e3c', fontWeight: '600' }}>+5</span>
                    </div>
                </div>

                {/* Slider Track */}
                <div
                    ref={sliderRef}
                    style={{
                        position: 'relative',
                        height: '60px',
                        margin: '0 10px',
                        cursor: 'pointer',
                        userSelect: 'none'
                    }}
                >
                    {/* Background gradient track */}
                    <div
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: 0,
                            right: 0,
                            height: '12px',
                            borderRadius: '6px',
                            background: 'linear-gradient(to right, #d32f2f 0%, #f57c00 25%, #757575 50%, #fbc02d 75%, #388e3c 100%)',
                            transform: 'translateY(-50%)',
                            boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.15)',
                            zIndex: 1
                        }}
                    />

                    {/* Center zero marker */}
                    <div
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            width: '4px',
                            height: '28px',
                            background: 'white',
                            transform: 'translate(-50%, -50%)',
                            borderRadius: '2px',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                            zIndex: 2
                        }}
                    />

                    {/* Tick marks */}
                    {[-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5].map(tick => (
                        <div
                            key={tick}
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: `${valueToPosition(tick)}%`,
                                width: '2px',
                                height: tick === 0 ? '20px' : '12px',
                                background: tick === 0 ? 'white' : 'rgba(255,255,255,0.4)',
                                transform: 'translate(-50%, -50%)',
                                borderRadius: '1px',
                                zIndex: tick === 0 ? 2 : 1
                            }}
                        />
                    ))}

                    {/* Option Thumbs */}
                    {options.map((option, idx) => {
                        const value = scores[option] || 0;
                        const position = valueToPosition(value);
                        const color = getRiskColor(value);

                        return (
                            <div key={idx}>
                                {/* Thumb */}
                                <div
                                    onMouseDown={() => handleMouseDown(option)}
                                    onTouchStart={() => handleTouchStart(option)}
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: `${position}%`,
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        background: 'white',
                                        border: `4px solid ${color}`,
                                        transform: 'translate(-50%, -50%)',
                                        cursor: 'grab',
                                        boxShadow: draggingOption === option
                                            ? '0 6px 20px rgba(0,0,0,0.3)'
                                            : '0 4px 12px rgba(0,0,0,0.2)',
                                        transition: draggingOption === option ? 'none' : 'all 0.2s ease',
                                        zIndex: draggingOption === option ? 20 : 10 + idx,
                                        userSelect: 'none'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!draggingOption) {
                                            e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.15)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!draggingOption) {
                                            e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)';
                                        }
                                    }}
                                >
                                    {/* Inner value display */}
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            fontSize: '0.7rem',
                                            fontWeight: '700',
                                            color: color,
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        {value}
                                    </div>
                                </div>

                                {/* Option label above thumb */}
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: '-35px',
                                        left: `${position}%`,
                                        transform: 'translateX(-50%)',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        padding: '4px 10px',
                                        borderRadius: '6px',
                                        fontSize: '0.75rem',
                                        fontWeight: '600',
                                        whiteSpace: 'nowrap',
                                        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                                        zIndex: 5,
                                        pointerEvents: 'none',
                                        opacity: draggingOption === option ? 1 : 0.9,
                                        transition: 'opacity 0.2s ease'
                                    }}
                                >
                                    {option}
                                </div>

                                {/* Risk label below thumb */}
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: '42px',
                                        left: `${position}%`,
                                        transform: 'translateX(-50%)',
                                        background: color,
                                        color: 'white',
                                        padding: '3px 8px',
                                        borderRadius: '4px',
                                        fontSize: '0.65rem',
                                        fontWeight: '600',
                                        whiteSpace: 'nowrap',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                                        zIndex: 5,
                                        pointerEvents: 'none',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    {getRiskLabel(value)}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Numeric scale below slider */}
                <div className="df jcsb mt3rem" style={{ paddingLeft: '10px', paddingRight: '10px', fontSize: '0.7rem', color: '#999' }}>
                    {[-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5].map(num => (
                        <span key={num} style={{
                            fontWeight: num === 0 ? '700' : '400',
                            color: num === 0 ? '#757575' : '#999',
                            minWidth: '20px',
                            textAlign: 'center'
                        }}>
                            {num}
                        </span>
                    ))}
                </div>
            </div>

            {/* Options Summary */}
            <div className="df fdc" style={{ gap: '0.75rem', marginBottom: '1.5rem' }}>
                {options.map((option, idx) => {
                    const value = scores[option] || 0;
                    const color = getRiskColor(value);

                    return (
                        <div
                            key={idx}
                            className="df aic jcsb br8px p0-75rem"
                            style={{
                                background: 'white',
                                border: `2px solid ${color}20`,
                                transition: 'all 0.3s ease'
                            }}
                        >
                            <div className="df aic" style={{ gap: '10px' }}>
                                <div
                                    style={{
                                        width: '12px',
                                        height: '12px',
                                        borderRadius: '50%',
                                        background: color,
                                        boxShadow: `0 0 8px ${color}40`
                                    }}
                                />
                                <span className="fs0-875rem fw600" style={{ color: '#333' }}>
                                    {option}
                                </span>
                            </div>
                            <div className="df aic" style={{ gap: '12px' }}>
                                <span
                                    className="fs0-75rem fw600"
                                    style={{
                                        background: `${color}20`,
                                        color: color,
                                        padding: '4px 10px',
                                        borderRadius: '6px'
                                    }}
                                >
                                    {getRiskLabel(value)}
                                </span>
                                <span
                                    className="fs1-1rem fw700"
                                    style={{
                                        color: color,
                                        minWidth: '35px',
                                        textAlign: 'right'
                                    }}
                                >
                                    {value > 0 ? '+' : ''}{value}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Risk Scale Indicators */}
            <div className="df jcsb mb1-5rem" style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                <div className="df fdc aic">
                    <TrendingDown style={{ fontSize: '16px', color: '#d32f2f', marginBottom: '4px' }} />
                    <span style={{ fontSize: '0.7rem', color: '#d32f2f', fontWeight: '600' }}>High Risk</span>
                </div>
                <div className="df fdc aic">
                    <Remove style={{ fontSize: '16px', color: '#757575', marginBottom: '4px' }} />
                    <span style={{ fontSize: '0.7rem', color: '#757575', fontWeight: '600' }}>Neutral</span>
                </div>
                <div className="df fdc aic">
                    <TrendingUp style={{ fontSize: '16px', color: '#388e3c', marginBottom: '4px' }} />
                    <span style={{ fontSize: '0.7rem', color: '#388e3c', fontWeight: '600' }}>Low Risk</span>
                </div>
            </div>

            {/* Legend */}
            <div className="br8px p1rem" style={{ background: 'white', border: '1px solid #e0e0e0' }}>
                <p className="fs0-75rem fw600 mb0-75rem" style={{ color: '#666', margin: '0 0 0.75rem 0' }}>
                    Risk Score Guide:
                </p>
                <div className="df aic jcsb" style={{ gap: '1rem', flexWrap: 'wrap' }}>
                    <div className="df aic" style={{ gap: '6px' }}>
                        <div style={{ width: '16px', height: '16px', borderRadius: '4px', background: '#d32f2f' }}></div>
                        <span style={{ fontSize: '0.75rem', color: '#666' }}>-5 to -3: Very High Risk</span>
                    </div>
                    <div className="df aic" style={{ gap: '6px' }}>
                        <div style={{ width: '16px', height: '16px', borderRadius: '4px', background: '#f57c00' }}></div>
                        <span style={{ fontSize: '0.75rem', color: '#666' }}>-2 to -1: Medium Risk</span>
                    </div>
                    <div className="df aic" style={{ gap: '6px' }}>
                        <div style={{ width: '16px', height: '16px', borderRadius: '4px', background: '#757575' }}></div>
                        <span style={{ fontSize: '0.75rem', color: '#666' }}>0: Neutral</span>
                    </div>
                    <div className="df aic" style={{ gap: '6px' }}>
                        <div style={{ width: '16px', height: '16px', borderRadius: '4px', background: '#fbc02d' }}></div>
                        <span style={{ fontSize: '0.75rem', color: '#666' }}>+1 to +2: Low Risk</span>
                    </div>
                    <div className="df aic" style={{ gap: '6px' }}>
                        <div style={{ width: '16px', height: '16px', borderRadius: '4px', background: '#388e3c' }}></div>
                        <span style={{ fontSize: '0.75rem', color: '#666' }}>+3 to +5: Very Low Risk</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RiskScoreSlider;
